<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Trash2,
  X,
} from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import LogFilterBar from '@/components/LogFilterBar.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDialog } from '@/composables/useDialog'
import AppLayout from '@/layouts/AppLayout.vue'
import { formatDate, formatDateTime } from '@/lib/utils'
import { clearLegacyApiLogsPath, legacyApiLogsPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'

interface ByPartnerStat {
  partner_id: string | null
  partner_name: string
  api_calls: number
  apply_redirects: number
  total: number
  last_seen: string | null
}

interface ByDateStat {
  date: string
  api_calls: number
  apply_redirects: number
}

interface Stats {
  total_api_calls: number
  total_apply_redirects: number
  by_partner: ByPartnerStat[]
  by_date: ByDateStat[]
}

interface LogEntry {
  id: number
  type: 'api_call' | 'apply_redirect'
  partner_id: string | null
  partner_name: string | null
  timestamp: string
  actor_id?: number | null
  token_name?: string | null
  application_id?: string | null
  outcome?: string | null
  referrer?: string | null
  customer_id?: string | null
}

interface PartnerOption {
  id: string
  name: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  date_from?: string | null
  date_to?: string | null
  partner_id?: string | null
}

const props = defineProps<{
  entries: LogEntry[]
  stats: Stats
  partner_options: PartnerOption[]
  pagination: PaginationMeta
  filters: Filters
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Legacy API Logs', href: legacyApiLogsPath() }]

const ALL = '__all__'

const search = ref(props.filters.q ?? '')
const partnerFilter = ref(props.filters.partner_id ?? ALL)
const dateFrom = ref(props.filters.date_from ?? '')
const dateTo = ref(props.filters.date_to ?? '')
const expandedId = ref<number | null>(null)

const hasActiveFilters = computed(
  () =>
    search.value !== '' ||
    partnerFilter.value !== ALL ||
    dateFrom.value !== '' ||
    dateTo.value !== '',
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  partner_id: partnerFilter.value !== ALL ? partnerFilter.value : undefined,
  date_from: dateFrom.value || undefined,
  date_to: dateTo.value || undefined,
}))

function applyFilters() {
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(legacyApiLogsPath(), params, { preserveState: true, replace: true })
}

let searchTimer: ReturnType<typeof setTimeout>

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch([partnerFilter, dateFrom, dateTo], applyFilters)

onUnmounted(() => clearTimeout(searchTimer))

function clearFilters() {
  search.value = ''
  partnerFilter.value = ALL
  dateFrom.value = ''
  dateTo.value = ''
}

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id
}

function typeVariantClass(type: 'api_call' | 'apply_redirect'): string {
  if (type === 'api_call') {
    return 'border-indigo-300 text-indigo-700 dark:border-indigo-700 dark:text-indigo-300'
  }
  return 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300'
}

function outcomeVariant(outcome: string | null | undefined): 'default' | 'secondary' | 'destructive' {
  if (outcome === 'created') return 'default'
  if (outcome === 'validation_error') return 'secondary'
  if (outcome === 'forbidden') return 'destructive'
  return 'secondary'
}

function truncateReferrer(url: string | null | undefined, maxLen = 60): string {
  if (!url) return '—'
  if (url.length <= maxLen) return url
  return url.substring(0, maxLen) + '…'
}

const recentByDate = computed(() => [...props.stats.by_date].reverse().slice(0, 14))

const logTableColumns: TableColumn[] = [
  { key: 'expand', label: '', headerClass: 'w-8' },
  { key: 'type', label: 'Type', headerClass: 'w-36' },
  { key: 'partner', label: 'Partner', headerClass: 'w-40' },
  { key: 'detail', label: 'Detail' },
  { key: 'timestamp', label: 'Timestamp', headerClass: 'w-40' },
]

const { confirm } = useDialog()

async function doClear() {
  const ok = await confirm({
    title: 'Clear legacy API logs?',
    description: 'This will permanently delete all log entries older than 3 days. This cannot be undone.',
    confirmLabel: 'Clear',
  })
  if (!ok) return
  router.delete(clearLegacyApiLogsPath(), { preserveState: false })
}
</script>

<template>
  <Head title="Legacy API Logs" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 p-4">
      <!-- Page header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Activity class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Legacy API Logs</h1>
            <p class="text-muted-foreground text-sm">Partner v1 API calls and redirected apply page visits</p>
          </div>
        </div>
        <Button variant="outline" size="sm" class="gap-1.5 text-destructive hover:text-destructive" @click="doClear()">
          <Trash2 class="size-3.5" />
          Clear
        </Button>
      </div>

      <!-- Stats section -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <CardTitle class="text-base">Activity Summary</CardTitle>
          <CardDescription>
            {{ stats.total_api_calls }} API call{{ stats.total_api_calls !== 1 ? 's' : '' }} ·
            {{ stats.total_apply_redirects }} apply redirect{{ stats.total_apply_redirects !== 1 ? 's' : '' }}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <!-- By Partner -->
            <div>
              <p class="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">By Partner</p>
              <div v-if="stats.by_partner.length === 0" class="text-muted-foreground text-sm">No data</div>
              <table v-else class="w-full text-sm">
                <thead>
                  <tr class="text-muted-foreground border-border border-b text-xs">
                    <th class="pb-1.5 text-left font-medium">Partner</th>
                    <th class="pb-1.5 text-right font-medium">API Calls</th>
                    <th class="pb-1.5 text-right font-medium">Redirects</th>
                    <th class="pb-1.5 text-right font-medium">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in stats.by_partner"
                    :key="row.partner_id ?? 'unknown'"
                    class="border-border border-b last:border-0"
                  >
                    <td class="py-1.5 pr-3 font-medium">{{ row.partner_name }}</td>
                    <td class="py-1.5 text-right tabular-nums">{{ row.api_calls }}</td>
                    <td class="py-1.5 text-right tabular-nums">{{ row.apply_redirects }}</td>
                    <td class="text-muted-foreground py-1.5 text-right">
                      {{ row.last_seen ? formatDate(row.last_seen) : '—' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Daily Activity -->
            <div>
              <p class="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">Daily Activity (last 14 days)</p>
              <div v-if="recentByDate.length === 0" class="text-muted-foreground text-sm">No data</div>
              <table v-else class="w-full text-sm">
                <thead>
                  <tr class="text-muted-foreground border-border border-b text-xs">
                    <th class="pb-1.5 text-left font-medium">Date</th>
                    <th class="pb-1.5 text-right font-medium">API Calls</th>
                    <th class="pb-1.5 text-right font-medium">Redirects</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in recentByDate"
                    :key="row.date"
                    class="border-border border-b last:border-0"
                  >
                    <td class="py-1.5 pr-3 font-mono text-xs">{{ row.date }}</td>
                    <td class="py-1.5 text-right tabular-nums">{{ row.api_calls }}</td>
                    <td class="py-1.5 text-right tabular-nums">{{ row.apply_redirects }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      <!-- Log table -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">Log Entries</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} entr{{ pagination.total_count !== 1 ? 'ies' : 'y' }}
                {{ hasActiveFilters ? 'found' : 'total' }}
              </CardDescription>
            </div>
            <Activity class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Filters -->
        <LogFilterBar
          v-model:search="search"
          v-model:date-from="dateFrom"
          v-model:date-to="dateTo"
          :has-active-filters="hasActiveFilters"
          search-placeholder="Search by partner or token…"
          @clear="clearFilters"
        >
          <Select v-model="partnerFilter">
            <SelectTrigger class="w-full sm:w-48">
              <SelectValue placeholder="Any partner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any partner</SelectItem>
              <SelectItem v-for="p in partner_options" :key="p.id" :value="p.id">{{ p.name }}</SelectItem>
            </SelectContent>
          </Select>
        </LogFilterBar>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div v-if="entries.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <Activity class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? 'No entries match your filters' : 'No log entries yet' }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable v-else :columns="logTableColumns" compact>
            <template v-for="entry in entries" :key="entry.id">
              <!-- Summary row -->
              <tr
                class="border-border cursor-pointer border-b transition-colors select-none"
                :class="expandedId === entry.id ? 'bg-muted/60' : 'hover:bg-muted/40'"
                @click="toggleExpand(entry.id)"
              >
                <td class="px-3 py-4 text-center">
                  <ChevronDown
                    v-if="expandedId === entry.id"
                    class="text-muted-foreground size-4 transition-transform"
                  />
                  <ChevronRight v-else class="text-muted-foreground size-4" />
                </td>
                <td class="px-4 py-4">
                  <Badge
                    variant="outline"
                    :class="typeVariantClass(entry.type)"
                    class="text-[10px]"
                  >
                    {{ entry.type === 'api_call' ? 'API Call' : 'Apply Redirect' }}
                  </Badge>
                </td>
                <td class="px-4 py-4 font-medium">{{ entry.partner_name ?? '—' }}</td>
                <td class="px-4 py-4">
                  <template v-if="entry.type === 'api_call'">
                    <div class="flex items-center gap-2">
                      <Badge v-if="entry.outcome" :variant="outcomeVariant(entry.outcome)" class="text-[10px]">
                        {{ entry.outcome }}
                      </Badge>
                      <span v-if="entry.token_name" class="text-muted-foreground text-xs">{{ entry.token_name }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <span class="text-muted-foreground font-mono text-xs">{{ truncateReferrer(entry.referrer) }}</span>
                  </template>
                </td>
                <td class="text-muted-foreground px-4 py-4 whitespace-nowrap">
                  {{ formatDateTime(entry.timestamp) }}
                </td>
              </tr>

              <!-- Expanded detail row -->
              <tr v-if="expandedId === entry.id" class="border-border border-b">
                <td colspan="5" class="bg-muted/30 px-6 py-4">
                  <div class="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-4">
                    <div v-if="entry.application_id">
                      <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Application ID</p>
                      <p class="mt-0.5 font-mono text-xs">{{ entry.application_id }}</p>
                    </div>
                    <div v-if="entry.type === 'api_call' && entry.actor_id">
                      <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Actor ID</p>
                      <p class="mt-0.5 font-mono text-xs">{{ entry.actor_id }}</p>
                    </div>
                    <div v-if="entry.type === 'api_call' && entry.token_name">
                      <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Token</p>
                      <p class="mt-0.5 text-xs">{{ entry.token_name }}</p>
                    </div>
                    <div v-if="entry.type === 'apply_redirect' && entry.customer_id">
                      <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Customer ID</p>
                      <p class="mt-0.5 font-mono text-xs">{{ entry.customer_id }}</p>
                    </div>
                    <div v-if="entry.type === 'apply_redirect' && entry.referrer">
                      <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Referrer</p>
                      <p class="mt-0.5 break-all font-mono text-xs">{{ entry.referrer }}</p>
                    </div>
                    <div v-if="entry.partner_id">
                      <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Partner ID</p>
                      <p class="mt-0.5 font-mono text-xs">{{ entry.partner_id }}</p>
                    </div>
                    <div>
                      <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Timestamp</p>
                      <p class="mt-0.5 font-mono text-xs">{{ entry.timestamp }}</p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="legacyApiLogsPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
